import AutosaveSuccess from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Divider } from "@mui/material";
import { get, includes, toLower } from "lodash";
import numeral from "numeral";
import React from "react";
import DealComplianceModal from "../../../common/js/components/DealComplianceModal";
import FormTitle from "../../../common/js/components/FormTitle";
import Comments from "../comments/CommentsContainer";
import CompCommentBlock from "../comments/CompCommentBlock";
import comments_constants from "../comments/constants";
import ListingCommentBlock from "../comments/ListingCommentBlock";
import { comp_contact_options } from "../contact/constants";
import ContactInformation from "../contact/ContactContainer";
import DealForm from "../deal/DealForm";
import InvoiceDetail from "../deal/InvoiceDetail";
import TotalConsiderationForm from "../deal/TotalConsiderationForm";
import DocumentDrop from "../documents/DocumentDropContainer";
import FinancialInformation from "../listing/FinancialInformation";
import ListingForm from "../listing/ListingForm";
import CondoForm from "../property/CondoForm";
import EducationalForm from "../property/EducationalForm";
import HospitalityForm from "../property/HospitalityForm";
import IndustrialForm from "../property/IndustrialForm";
import LandForm from "../property/LandForm";
import MixedUseForm from "../property/MixedUseForm";
import MultifamilyForm from "../property/MultifamilyForm";
import ParkingInfoForm from "../property/ParkingInfoForm";
import PortfolioForm from "../property/PortfolioForm";
import Property from "../property/Property";
import PropertyUpdateModalContainer from "../property/PropertyUpdateModalContainer";
import ReligiousForm from "../property/ReligiousForm";
import RetailForm from "../property/RetailForm";
import { formatDollarPreserveNil } from "../utils/operations";
import LeaseDetailForm from "./LeaseDetailForm";

const AppraisalCompComponent = (props) => {
    const {
        deal,
        disableInputs,
        documentWarning,
        errors,
        handleAddArrayElement,
        handleAutocalculateTiSf,
        handleDeleteArrayElement,
        handleUpdateArrayElement,
        handleConfirmWarning,
        handleDocumentWarning,
        handleInput,
        handleObjectPropertyChange,
        id,
        listingAttributes,
        property,
        restrictViewDocuments,
        toggleTerminationDoc,
        panels,
        togglePanel,
        compRef,
    } = props;

    const calculateRatio = (dividendVal, divisorVal) => {
        let dividend = numeral(dividendVal);
        let divisor = numeral(divisorVal);
        let quotient = null;
        if (
            dividend.value() &&
            divisor.value() &&
            !isNaN(dividend.value()) &&
            !isNaN(divisor.value()) &&
            divisor.value() > 0
        ) {
            quotient = dividend.divide(divisor.value());
        }
        return quotient;
    };

    const processHandleInput = (id, value) => {
        props.handleInput(id, value);
        if (id === "listing.attributes.roomCount" || id === "listing.attributes.multifamilyUnitCount") {
            calculateSalePricePerUnit(get(props.deal, "totalConsiderationAmount", null), value);
        }
    };

    const processHandleDealChange = (e) => {
        props.handleDealChange(e);
        if (e.target.id === "totalConsiderationAmount") {
            handleTotalConsiderationChange(e);
        } else if (e.target.id === "squareFeet") {
            handleBuildingSfChange(e);
        } else if (e.target.id === "transactionLandSf") {
            handleLandSfChange(e);
        } else if (e.target.id === "saleAdjustment") {
            calculateAnalysisPrice(get(props.deal, "totalConsiderationAmount", null), e.target.value);
        } else if (e.target.id === "noi") {
            calculateNoiPerSf(e.target.value, get(props.deal, "squareFeet"));
        }
    };

    const handleLandSfChange = (e) => {
        calculateLandPricePerSf(get(props.deal, "totalConsiderationAmount", null), e.target.value);
        calculateLandToBldgRatio(e.target.value, get(props.deal, "squareFeet", null));
    };

    const handleTotalConsiderationChange = (e) => {
        calculateBldgPricePerSf(e.target.value, get(props.deal, "squareFeet", null));
        calculateLandPricePerSf(e.target.value, get(props.deal, "transactionLandSf", null));
        calculateAnalysisPrice(e.target.value, get(props.deal, "saleAdjustment", null));

        if (get(props.deal, "propertyType", null) === "Hospitality") {
            calculateSalePricePerUnit(e.target.value, get(props.deal, "listing.attributes.roomCount", null));
        } else if (get(props.deal, "propertyType", null) === "Multifamily") {
            calculateSalePricePerUnit(e.target.value, get(props.deal, "listing.attributes.multifamilyUnitCount", null));
        } else {
            props.handleInput("salePricePerUnit", "");
        }
    };

    const handleBuildingSfChange = (e) => {
        handleAutocalculateTiSf(e);
        calculateLandToBldgRatio(get(props.deal, "transactionLandSf", null), e.target.value);
        calculateBldgPricePerSf(get(props.deal, "totalConsiderationAmount", null), e.target.value);
        calculateNoiPerSf(get(props.deal, "noi"), e.target.value);
    };

    const calculateAnalysisPrice = (price, adjustmentAmount) => {
        if (toLower(get(props.deal, "transactionType", "")) === "sale") {
            let analysisPrice = numeral(price);
            let adjustment = numeral(adjustmentAmount);
            if (!isNaN(analysisPrice.value()) && !isNaN(adjustment.value())) {
                analysisPrice = analysisPrice.value() + adjustment.value();
            } else if (!isNaN(analysisPrice.value())) {
                analysisPrice = analysisPrice.value();
            } else {
                analysisPrice = null;
            }
            props.handleInput("analysisPrice", formatDollarPreserveNil(analysisPrice));
        }
    };

    const calculateLandToBldgRatio = (landSf, bldgSf) => {
        let landToBldgRatio = calculateRatio(landSf, bldgSf);
        props.handleInput("landToBldgRatio", numeral(landToBldgRatio).format("0.00"));
    };

    const calculateBldgPricePerSf = (price, sf) => {
        if (toLower(get(props.deal, "transactionType", "")) === "sale") {
            let bldgSalePricePerSf = calculateRatio(price, sf);
            props.handleInput("bldgSalePricePerSf", formatDollarPreserveNil(bldgSalePricePerSf));
        }
    };

    const calculateLandPricePerSf = (price, sf) => {
        if (toLower(get(props.deal, "transactionType", "")) === "sale") {
            let landSalePricePerSf = calculateRatio(price, sf);
            props.handleInput("landSalePricePerSf", formatDollarPreserveNil(landSalePricePerSf));
        }
    };

    const calculateSalePricePerUnit = (price, units) => {
        if (toLower(get(props.deal, "transactionType", "")) === "sale") {
            let salePricePerUnit = calculateRatio(price, units);
            props.handleInput("salePricePerUnit", salePricePerUnit ? formatDollarPreserveNil(salePricePerUnit) : "");
        }
    };

    const calculateNoiPerSf = (noi, sf) => {
        let noiPerSf = calculateRatio(noi, sf);
        props.handleInput("noiPerSf", noiPerSf ? formatDollarPreserveNil(noiPerSf) : "");
    };

    const handleTriggerPropertySearchModal = (e) => {
        e.stopPropagation();
        props.togglePropertySearchModal(true);
    };

    return (
        <div ref={compRef}>
            <DocumentDrop
                entityKey={id}
                entityStatus={deal?.compStatus}
                type="comp"
                documentTypes="comp"
                formDisabled={disableInputs}
                errors={errors.documents}
                restrictPreview={restrictViewDocuments}
                toggleTerminationDoc={toggleTerminationDoc}
                parentType="COMP"
                stateCode={get(deal, "listing.property.primaryAddress.city.state.stateCode", null)}
            />
            <div>
                <Accordion
                    expanded={panels.property}
                    onChange={() => togglePanel("property", !panels.property)}
                    style={{ marginBottom: "35px" }}
                >
                    {(get(props.userProfile, "role") === "dataAdmin" && get(props.listing, "status") === "Active") ||
                    disableInputs === false ? (
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div className="expansionPanelContentContainer">
                                <AutosaveSuccess className="waterloo" style={{ paddingTop: "4px" }} />

                                <div>
                                    Property Information
                                    <EditIcon
                                        style={{
                                            width: "18px",
                                            height: "18px",
                                            fill: "#233A47",
                                            paddingLeft: "5px",
                                        }}
                                        onClick={(e) => handleTriggerPropertySearchModal(e)}
                                    />
                                    <div>
                                        <small className="entityIDText">Property ID: {property.propertyKey}</small>
                                    </div>
                                </div>
                            </div>
                        </AccordionSummary>
                    ) : (
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div className="expansionPanelContentContainer">
                                <AutosaveSuccess className="waterloo" />
                                <div>
                                    Property Information
                                    <div>
                                        <small className="entityIDText">Property ID: {property.propertyKey}</small>
                                    </div>
                                </div>
                            </div>
                        </AccordionSummary>
                    )}
                    <Divider />
                    <AccordionDetails>
                        <Property
                            attributes={get(deal, "listing.property.propertyAttributes", {})}
                            property={get(deal, "listing.property", {})}
                            handleInputChange={(f, v) => handleInput("listing." + f, v)}
                            errors={errors}
                            handleAddArrayElement={(path) => props.handleAddArrayElement("listing." + path)}
                            handleDeleteArrayElement={(path, index) =>
                                props.handleDeleteArrayElement("listing." + path, index)
                            }
                            handleUpdateArrayElement={(arrayPath, index, field, value) =>
                                props.handleUpdateArrayElement("listing." + arrayPath, index, field, value)
                            }
                            deal={deal}
                            prefix="listing."
                            disableInputs={get(deal, "listing.property.propertyKey", null) !== null}
                            propertyType={
                                get(deal, "listing.property.propertyType", null) !== null
                                    ? get(deal, "listing.property.propertyType", null)
                                    : get(deal, "propertyType", null)
                            }
                        />
                    </AccordionDetails>
                </Accordion>
                <div>
                    <PropertyUpdateModalContainer {...props} formType="comp" />
                </div>
            </div>

            {/*                         Listing Information                         */}

            {get(deal, "listing.listingName") !== undefined && (
                <React.Fragment>
                    <Accordion
                        expanded={panels.listingInformation}
                        onChange={() => togglePanel("listingInformation", !panels.listingInformation)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{ display: "flex", gap: "1vw", alignItems: "center" }}>
                                <AutosaveSuccess className="waterloo" />
                                <div>
                                    Listing Information
                                    <div>
                                        <small className="entityIDText">LISTING ID: {deal.listing.listingKey}</small>
                                    </div>
                                </div>
                            </div>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails>
                            <ListingForm
                                {...props}
                                isOutside={includes(["Outside Draft", "Outside"], get(deal, "compStatus", ""))}
                                disabled={disableInputs}
                                listing={get(deal, "listing", {})}
                                listingBrokers={get(deal, "listing.listingBrokers", [{}])}
                                propertyType={get(deal, "listing.attributes.useType", null)}
                                errors={get(errors, "listing", {})}
                                showListingExpiration={false}
                                handleInputChange={(field, value) => handleInput(field, value)}
                                handleAddArrayElement={(path, obj) => handleAddArrayElement("listing." + path, obj)}
                                handleDeleteArrayElement={(path, index) => handleDeleteArrayElement(path, index)}
                                handleUpdateArrayElement={(path, index, field, value) =>
                                    handleUpdateArrayElement(path, index, field, value)
                                }
                                compStatus={get(deal, "compStatus", null)}
                                disableBrokerFields={true}
                            />
                            <Divider />
                            <MixedUseForm
                                {...props}
                                onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                data={listingAttributes}
                                dataPathPrefix="listing.attributes"
                                errors={get(errors, "attributes", {})}
                            />
                            <Divider />
                            <PortfolioForm
                                {...props}
                                onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                data={listingAttributes}
                                dataPathPrefix="listing.attributes"
                                errors={get(errors, "attributes", {})}
                            />
                            <Divider />
                            <CondoForm
                                {...props}
                                onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                data={listingAttributes}
                                dataPathPrefix="listing.attributes"
                                errors={get(errors, "attributes", {})}
                            />
                            <Divider />
                            <FormTitle title="Parking Information" />
                            <ParkingInfoForm
                                {...props}
                                context="listing"
                                data={listingAttributes}
                                onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                errors={get(errors, "listing.attributes", {})}
                            />
                            {toLower(deal.propertyType) === "industrial" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Industrial Information" />
                                    <IndustrialForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            {toLower(deal.propertyType) === "land" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Land Information" />
                                    <LandForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            {toLower(deal.propertyType) === "multifamily" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Multifamily Information" />
                                    <MultifamilyForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) =>
                                            processHandleInput("listing.attributes." + field, value)
                                        }
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            {toLower(deal.propertyType) === "retail" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Retail Information" />
                                    <RetailForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) => handleInput("listing.attributes." + field, value)}
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        dataPathPrefix="listing.attributes"
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            {toLower(deal.propertyType) === "hospitality" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Hospitality Information" />
                                    <HospitalityForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) =>
                                            processHandleInput("listing.attributes." + field, value)
                                        }
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            {toLower(deal.propertyType) === "religious" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Religious Information" />
                                    <ReligiousForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) =>
                                            processHandleInput("listing.attributes." + field, value)
                                        }
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            {toLower(deal.propertyType) === "educational" && (
                                <div>
                                    <Divider />
                                    <FormTitle title="Educational Information" />
                                    <EducationalForm
                                        {...props}
                                        context="listing"
                                        onChange={(field, value) =>
                                            processHandleInput("listing.attributes." + field, value)
                                        }
                                        data={listingAttributes}
                                        errors={get(errors, "listing.attributes", {})}
                                        listingStatus={get(deal, "listing.status", null)}
                                        transactionType={deal.transactionType}
                                    />
                                </div>
                            )}
                            <Divider />
                            <ListingCommentBlock
                                errors={errors}
                                handleInputChange={handleInput}
                                data={get(deal, "listing")}
                                disableInputs={true}
                                showFormTitle={true}
                            />
                        </AccordionDetails>
                    </Accordion>

                    {/*                        Financial Information */}

                    <Accordion
                        expanded={panels.listingFinancial}
                        onChange={() => togglePanel("listingFinancial", !panels.listingFinancial)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div className="expansionPanelContentContainer">
                                <AutosaveSuccess className="waterloo" style={{ paddingTop: "4px" }} />
                                <div>Listing Financial Information</div>
                            </div>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails>
                            <FinancialInformation
                                {...props}
                                disableInputs={disableInputs}
                                listing={deal.listing}
                                errors={get(errors, "listing", {})}
                                handleInputChange={(field, value) => handleInput(field, value)}
                                compStatus={get(deal, "compStatus", null)}
                            />
                        </AccordionDetails>
                    </Accordion>
                </React.Fragment>
            )}

            {/*                        Comp Information                         */}

            <Accordion
                expanded={panels.compInformation}
                onChange={() => togglePanel("compInformation", !panels.compInformation)}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div>
                        Comp Information
                        <div>
                            <small className="entityIDText">Deal ID: {deal.dealKey}</small>
                        </div>
                    </div>
                </AccordionSummary>
                <Divider />
                <AccordionDetails>
                    <DealForm
                        {...props}
                        disableInputs={disableInputs}
                        transactionType={deal.transactionType}
                        handleInput={(field, value) => handleInput(field, value)}
                        errors={errors}
                        handleDealChange={processHandleDealChange}
                        isAppraisalComp={true}
                    />
                </AccordionDetails>
            </Accordion>

            {/*                        Total Consideration                         */}

            {toLower(deal.transactionType) !== "sale" && (
                <Accordion
                    expanded={panels.totalConsideration}
                    onChange={() => togglePanel("totalConsideration", !panels.totalConsideration)}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Total Consideration</AccordionSummary>
                    <Divider />
                    <AccordionDetails>
                        <TotalConsiderationForm
                            {...props}
                            disableInputs={disableInputs}
                            transactionType={deal.transactionType}
                            handleAutocalculateTiSf={processHandleDealChange}
                            handleTotalConsiderationAmountChange={(value) =>
                                handleInput("totalConsiderationAmount", value)
                            }
                        />
                        <InvoiceDetail {...props} disableInputs={disableInputs} />
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Lease Details */}

            {toLower(deal.transactionType) === "lease" && (
                <Accordion
                    expanded={panels.leaseDetails}
                    onChange={() => togglePanel("leaseDetails", !panels.leaseDetails)}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Lease Details</AccordionSummary>
                    <Divider />
                    {deal.transactionType && (
                        <AccordionDetails>
                            <LeaseDetailForm
                                {...props}
                                onChange={(event) => handleObjectPropertyChange("deal", event)}
                                leaseDetail={deal}
                                disableInputs={disableInputs}
                                transactionType={deal.transactionType}
                                useType={deal.propertyType}
                                errors={errors}
                                handleAutocalculateTiSf={handleAutocalculateTiSf}
                            />
                        </AccordionDetails>
                    )}
                </Accordion>
            )}

            {/* Comp Contact Information */}

            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Comp Contact Information</AccordionSummary>
                <Divider />
                <AccordionDetails>
                    <ContactInformation
                        {...props}
                        handleChange={(field, value, index) =>
                            handleInput(`propertyAttributes.contacts[${index}].${field}`, value)
                        }
                        handleAdd={() => handleAddArrayElement("propertyAttributes.contacts")}
                        handleDelete={(index) => handleDeleteArrayElement("propertyAttributes.contacts", index)}
                        contacts={get(deal, "propertyAttributes.contacts", [])}
                        contactTypeOptions={comp_contact_options}
                        addButtonText="Add Contact Information"
                        includeInvoiceRecipientCheckbox={false}
                        maxContacts={5}
                        displayWhenEmpty={true}
                        companyFieldAvailable={true}
                        errors={errors}
                        hideTitle={true}
                        freeFormCompany={true}
                    />
                </AccordionDetails>
            </Accordion>

            <CompCommentBlock data={deal} disableInputs={disableInputs} handleInputChange={handleInput} />
            <Comments parentRecordType={comments_constants.COMP_PARENT_RECORD_TYPE} parentRecordId={id} />
            <DealComplianceModal
                documentWarning={documentWarning}
                handleDocumentWarning={handleDocumentWarning}
                handleConfirmWarning={handleConfirmWarning}
            />
        </div>
    );
};

export default AppraisalCompComponent;
